import { RelatedEntityType } from './notification.model';

export interface SupportTicket {
  id: number;
  ticketNumber: string;
  title: string;
  titleKey?: string;
  description: string;
  descriptionKey?: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  subcategory?: string;
  assignedTo?: number;
  createdBy: number;
  createdDate: Date;
  updatedDate: Date;
  resolvedDate?: Date;
  closedDate?: Date;
  dueDate?: Date;
  estimatedResolutionDate?: Date;
  actualResolutionTime?: number;
  tags?: string[];
  attachments?: TicketAttachment[];
  comments?: TicketComment[];
  relatedEntityType?: RelatedEntityType;
  relatedEntityId?: number;
  customerSatisfaction?: CustomerSatisfaction;
  internalNotes?: string;
  isEscalated: boolean;
  escalationReason?: string;
  escalationDate?: Date;
  slaBreach: boolean;
  slaBreachReason?: string;
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  PENDING_CUSTOMER = 'pending_customer',
  PENDING_VENDOR = 'pending_vendor',
  PENDING_APPROVAL = 'pending_approval',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
  CANCELLED = 'cancelled',
  DUPLICATE = 'duplicate',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
  CRITICAL = 'critical',
}

export enum TicketCategory {
  TECHNICAL = 'technical',
  BILLING = 'billing',
  GENERAL = 'general',
  FEATURE_REQUEST = 'feature_request',
  BUG_REPORT = 'bug_report',
  ACCOUNT = 'account',
  SECURITY = 'security',
  INTEGRATION = 'integration',
  PERFORMANCE = 'performance',
  OTHER = 'other',
}

export interface TicketAttachment {
  id: number;
  ticketId: number;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  uploadedBy: number;
  uploadedDate: Date;
  isDeleted: boolean;
  deletedDate?: Date;
}

export interface TicketComment {
  id: number;
  ticketId: number;
  authorId: number;
  content: string;
  isInternal: boolean;
  isSystemGenerated: boolean;
  createdDate: Date;
  updatedDate: Date;
  attachments?: TicketAttachment[];
  mentions?: number[];
}

export interface CustomerSatisfaction {
  id: number;
  ticketId: number;
  rating: number;
  feedback?: string;
  submittedDate: Date;
  isAnonymous: boolean;
}

export interface TicketSLA {
  id: number;
  category: TicketCategory;
  priority: TicketPriority;
  responseTime: number;
  resolutionTime: number;
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
}

export interface TicketEscalation {
  id: number;
  ticketId: number;
  escalatedFrom: number;
  escalatedTo: number;
  reason: string;
  escalatedDate: Date;
  resolvedDate?: Date;
  isActive: boolean;
}

export interface TicketTemplate {
  id: number;
  name: string;
  description?: string;
  category: TicketCategory;
  subcategory?: string;
  titleTemplate: string;
  descriptionTemplate: string;
  priority: TicketPriority;
  assignedTo?: number;
  tags?: string[];
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
}

export interface TicketWorkflow {
  id: number;
  name: string;
  description?: string;
  category: TicketCategory;
  steps: WorkflowStep[];
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
}

export interface WorkflowStep {
  id: number;
  workflowId: number;
  stepName: string;
  stepOrder: number;
  assignedRole?: number;
  assignedUser?: number;
  status: TicketStatus;
  isRequired: boolean;
  estimatedTime?: number;
  conditions?: WorkflowCondition[];
  actions?: WorkflowAction[];
}

export interface WorkflowCondition {
  id: number;
  stepId: number;
  field: string;
  operator: ConditionOperator;
  value: any;
  isActive: boolean;
}

export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
}

export interface WorkflowAction {
  id: number;
  stepId: number;
  actionType: ActionType;
  parameters: Record<string, any>;
  isActive: boolean;
}

export enum ActionType {
  ASSIGN_USER = 'assign_user',
  ASSIGN_ROLE = 'assign_role',
  CHANGE_STATUS = 'change_status',
  CHANGE_PRIORITY = 'change_priority',
  SEND_NOTIFICATION = 'send_notification',
  ADD_TAG = 'add_tag',
  REMOVE_TAG = 'remove_tag',
  SET_DUE_DATE = 'set_due_date',
  ESCALATE = 'escalate',
  AUTO_RESOLVE = 'auto_resolve',
}
